import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import AdminPortalShell from "@/components/admin/AdminPortalShell";
import QuestionFilterBar, {
  type Filters,
} from "@/components/admin/QuestionFilterBar";
import QuestionListTable from "@/components/admin/QuestionListTable";
import {
  deleteAdminQuestion,
  listAdminQuestions,
  type AdminListPage,
} from "@/lib/admin-api";
import { ApiError } from "@/lib/api";
import { track } from "@/lib/analytics";

/**
 * apps/web :: pages :: AdminQuestionListPage
 *
 * `/admin/questions` — filter + paginated table of the practice question
 * bank. Owns the filter state + pagination offset; delegates presentation
 * to `QuestionFilterBar` and `QuestionListTable`. Refetches on any filter
 * mutation (resetting offset to 0 so the user doesn't land on an empty
 * page) and on explicit pagination Next/Previous.
 */
const PAGE_LIMIT = 50;
const EMPTY_FILTERS: Filters = { state: "", category: "", locale: "" };

interface PageState {
  kind: "loading" | "ready" | "error";
  page?: AdminListPage;
  errorMessage?: string;
}

export default function AdminQuestionListPage() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [offset, setOffset] = useState(0);
  const [state, setState] = useState<PageState>({ kind: "loading" });
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setState({ kind: "loading" });
    try {
      const page = await listAdminQuestions({
        state: filters.state || undefined,
        category: filters.category || undefined,
        locale: filters.locale || undefined,
        limit: PAGE_LIMIT,
        offset,
      });
      setState({ kind: "ready", page });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : String(err);
      setState({ kind: "error", errorMessage: message });
    }
  }, [filters.state, filters.category, filters.locale, offset]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  // Analytics: fire once per mount.
  useEffect(() => {
    track("admin.page_viewed", { page: "list" });
  }, []);

  const onFiltersChange = (next: Filters) => {
    setFilters(next);
    setOffset(0);
  };

  const onDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await deleteAdminQuestion(id);
      track("admin.question.deleted", { question_id_prefix: id.slice(0, 8) });
      await refetch();
    } catch {
      // Surface failures via the existing error state on refetch; we don't
      // need a bespoke delete toast because the absence-of-removal in the
      // next refetch is the source of truth.
      await refetch();
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <AdminPortalShell
      title={t("admin.title")}
      subtitle={t("admin.subtitle")}
      action={
        <Link
          to="/admin/questions/new"
          data-testid="admin-list-create-cta"
          className="inline-flex items-center rounded-md border border-brand-primary bg-brand-primary px-md py-sm text-sm font-semibold text-white no-underline transition-colors hover:bg-brand-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
        >
          {t("admin.list.createCta")}
        </Link>
      }
    >
      <div className="space-y-md">
        <QuestionFilterBar
          value={filters}
          onChange={onFiltersChange}
          disabled={state.kind === "loading"}
        />

        {state.kind === "loading" && (
          <div
            role="status"
            aria-live="polite"
            data-testid="admin-list-loading"
            className="rounded-md border border-neutral-200 bg-white p-lg text-center text-sm text-neutral-700"
          >
            {t("admin.gate.loading")}
          </div>
        )}

        {state.kind === "error" && (
          <div
            role="alert"
            data-testid="admin-list-error"
            className="rounded-md border border-danger/40 bg-danger/5 p-md text-sm text-danger"
          >
            {t("admin.list.loadError", { message: state.errorMessage })}
          </div>
        )}

        {state.kind === "ready" && state.page && (
          <QuestionListTable
            questions={state.page.questions}
            totalCount={state.page.total_count}
            limit={PAGE_LIMIT}
            offset={offset}
            onPageChange={setOffset}
            onDelete={onDelete}
            deletingId={deletingId}
          />
        )}
      </div>
    </AdminPortalShell>
  );
}
